/**
 * ChainVault WP - Transactions Page
 *
 * @package ChainVault
 * @since   1.0.0
 */

import React, { useState, useEffect } from 'react';
import { useWallet } from '../context/WalletContext';
import { formatTimestamp, shortenHash } from '../utils/etherscan';
import { playSendSound, playErrorSound, resumeAudio } from '../utils/sounds';
import toast from 'react-hot-toast';

function TransactionsPage() {

    const {
        address,
        balances,
        selectedNetwork,
        currentNetwork,
        transactions,
        isLoadingTransactions,
        getUSDValue,
        formatAddress,
        connectMetaMask,
        isConnecting,
        walletType,
        builtInWallet,
        sendFromBuiltIn,
    } = useWallet();

    const [ searchQuery,       setSearchQuery       ] = useState( '' );
    const [ filter,            setFilter            ] = useState( 'all' );
    const [ showDepositModal,  setShowDepositModal  ] = useState( false );
    const [ showSendModal,     setShowSendModal     ] = useState( false );
    const [ sendTo,            setSendTo            ] = useState( '' );
    const [ sendAmount,        setSendAmount        ] = useState( '' );
    const [ isSending,         setIsSending         ] = useState( false );

    const currentBalance = balances[ selectedNetwork ] || '0.000000';
    const usdValue       = getUSDValue( currentBalance, selectedNetwork );

    const getDepositAddress = () => {
        if ( walletType === 'builtin' && builtInWallet ) {
            if ( selectedNetwork === 'solana' )  return builtInWallet.solana.address;
            if ( selectedNetwork === 'bitcoin' ) return builtInWallet.bitcoin.address;
            return builtInWallet.ethereum.address;
        }
        return address;
    };
    const depositAddress = getDepositAddress();

    const copyAddress = () => {
        if ( depositAddress ) {
            navigator.clipboard.writeText( depositAddress );
            toast.success( 'Address copied!' );
        }
    };

    // WalletContext handles loading transactions automatically on network/address change
    // No need to call loadTransactions here — doing so causes double-loading and flash

    const filteredTransactions = transactions.filter( tx => {
        const matchesSearch = searchQuery === '' ||
            tx.hash?.toLowerCase().includes( searchQuery.toLowerCase() ) ||
            tx.to?.toLowerCase().includes( searchQuery.toLowerCase() ) ||
            tx.from?.toLowerCase().includes( searchQuery.toLowerCase() ) ||
            tx.signature?.toLowerCase().includes( searchQuery.toLowerCase() );

        const isSolana   = selectedNetwork === 'solana';
        const isSent     = isSolana
            ? ( tx.isSent === true || tx.from?.toLowerCase() === address?.toLowerCase() )
            : tx.from?.toLowerCase() === address?.toLowerCase();
        const isReceived = isSolana
            ? ( tx.isSent === false || tx.to?.toLowerCase() === address?.toLowerCase() )
            : tx.to?.toLowerCase() === address?.toLowerCase();

        const matchesFilter = filter === 'all' ||
            ( filter === 'sent'     && isSent     ) ||
            ( filter === 'received' && isReceived );

        return matchesSearch && matchesFilter;
    } );

    const handleSend = async () => {
        if ( ! sendTo ) { toast.error( 'Please enter a recipient address.' ); return; }
        if ( ! sendAmount || parseFloat( sendAmount ) <= 0 ) { toast.error( 'Please enter a valid amount.' ); return; }

        setIsSending( true );
        try {
            if ( walletType === 'builtin' ) {
                const txHash = await sendFromBuiltIn( sendTo, sendAmount, selectedNetwork );
                await resumeAudio();
                playSendSound();
                toast.success( 'Transaction sent! ' + txHash.slice( 0, 16 ) + '...' );
                setShowSendModal( false );
                setSendTo( '' );
                setSendAmount( '' );
            } else if ( walletType === 'metamask' ) {
                const { ethers } = await import( 'ethers' );
                const provider   = new ethers.BrowserProvider( window.ethereum );
                const signer     = await provider.getSigner();
                const tx         = await signer.sendTransaction( {
                    to:    sendTo,
                    value: ethers.parseEther( sendAmount.toString() ),
                } );
                await tx.wait();
                await resumeAudio();
                playSendSound();
                toast.success( 'Sent! TX: ' + tx.hash.slice( 0, 16 ) + '...' );
                setShowSendModal( false );
                setSendTo( '' );
                setSendAmount( '' );
            } else {
                await resumeAudio();
                playSendSound();
                toast.success( 'Transaction submitted!' );
                setShowSendModal( false );
            }
        } catch ( err ) {
            console.error( err );
            playErrorSound();
            toast.error( 'Transaction failed: ' + err.message );
        } finally {
            setIsSending( false );
        }
    };

    return (
        <div className="cv-page">
            <div className="cv-wallet-layout">

                { /* Left Panel */ }
                <div className="cv-wallet-left">
                    <div className="cv-balance-card">
                        <div style={{ fontSize: '13px', color: '#888', marginBottom: '8px' }}>
                            Your total balance
                        </div>
                        <div className="cv-balance-amount">
                            <span className="cv-balance-symbol">
                                { currentNetwork.symbol === 'ETH' ? 'Ξ' :
                                  currentNetwork.symbol === 'BTC' ? '₿' :
                                  currentNetwork.symbol === 'SOL' ? '◎' :
                                  currentNetwork.symbol }
                            </span>
                            <span className="cv-balance-number">{ currentBalance }</span>
                        </div>
                        { address && (
                            <div style={{ fontSize: '13px', color: '#888', marginBottom: '12px' }}>
                                ≈ { usdValue } USD
                            </div>
                        ) }
                        <div className="cv-balance-actions">
                            <button
                                className="cv-action-btn primary"
                                onClick={ () => setShowDepositModal( true ) }
                            >
                                Deposit
                            </button>
                            <button
                                className="cv-action-btn secondary"
                                onClick={ () => setShowSendModal( true ) }
                                disabled={ ! address }
                            >
                                Send
                            </button>
                        </div>
                    </div>

                    <div className="cv-faq-card">
                        <h3>FAQ</h3>
                        <details className="cv-faq-item">
                            <summary>How are my private keys stored?</summary>
                            <p>Your private keys are stored securely in your browser and never sent to any server.</p>
                        </details>
                        <details className="cv-faq-item">
                            <summary>What are the fees involved?</summary>
                            <p>A small service fee is charged on each transaction. Network gas fees also apply.</p>
                        </details>
                        <details className="cv-faq-item">
                            <summary>Why is mining fee too high?</summary>
                            <p>Mining fees depend on network congestion. Try again when the network is less busy.</p>
                        </details>
                    </div>
                </div>

                { /* Right Panel */ }
                <div className="cv-wallet-right">
                    <div className="cv-transactions-panel">
                        <div className="cv-panel-header">
                            <h2>Transactions</h2>
                            <div className="cv-search-bar">
                                <input
                                    type="text"
                                    className="cv-input"
                                    placeholder="Search by hash or address..."
                                    value={ searchQuery }
                                    onChange={ e => setSearchQuery( e.target.value ) }
                                />
                                <div className="cv-filter-tabs">
                                    { [ 'all', 'sent', 'received' ].map( f => (
                                        <button
                                            key={ f }
                                            className={ `cv-filter-tab ${ filter === f ? 'active' : '' }` }
                                            onClick={ () => setFilter( f ) }
                                        >
                                            { f.charAt( 0 ).toUpperCase() + f.slice( 1 ) }
                                        </button>
                                    ) ) }
                                </div>
                            </div>
                        </div>

                        { ! address ? (
                            <div className="cv-empty-state">
                                <div className="cv-empty-placeholder">
                                    <div className="cv-placeholder-row"></div>
                                    <div className="cv-placeholder-row short"></div>
                                    <div className="cv-placeholder-row"></div>
                                </div>
                                <p className="cv-empty-title">Connect your wallet</p>
                                <p className="cv-empty-subtitle">Connect your wallet to see your transaction history</p>
                                <button className="cv-btn-primary" onClick={ connectMetaMask } disabled={ isConnecting } style={{ marginTop: '16px' }}>
                                    { isConnecting ? 'Connecting...' : 'Connect Wallet' }
                                </button>
                            </div>
                        ) : isLoadingTransactions ? (
                            <div className="cv-empty-state">
                                <p className="cv-empty-title" style={{ animation: 'pulse 1.5s infinite' }}>Loading transactions...</p>
                                <p className="cv-empty-subtitle">Fetching your transaction history from the blockchain</p>
                            </div>
                        ) : filteredTransactions.length === 0 ? (
                            <div className="cv-empty-state">
                                <div className="cv-empty-placeholder">
                                    <div className="cv-placeholder-row"></div>
                                    <div className="cv-placeholder-row short"></div>
                                    <div className="cv-placeholder-row"></div>
                                </div>
                                <p className="cv-empty-title">No transactions</p>
                                <p className="cv-empty-subtitle">
                                    { filter !== 'all' ? 'No ' + filter + ' transactions found' : 'No transactions found on this network' }
                                </p>
                            </div>
                        ) : (
                            <div className="cv-transaction-list">
                                { filteredTransactions.map( ( tx, index ) => {
                                    const isSolana = selectedNetwork === 'solana';
                                    const isSent   = isSolana
                                        ? ( tx.isSent === true || tx.from?.toLowerCase() === address?.toLowerCase() )
                                        : tx.from?.toLowerCase() === address?.toLowerCase();
                                    const txHash   = tx.hash || tx.signature || '';
                                    const txTo     = tx.to   || tx.destination || '';
                                    const txFrom   = tx.from || tx.source      || '';
                                    return (
                                        <div key={ index } className="cv-transaction-row">
                                            <div className="cv-tx-icon">{ tx.isError ? '❌' : isSent ? '📤' : '📥' }</div>
                                            <div className="cv-tx-info">
                                                <span className="cv-tx-type">{ tx.isError ? 'Failed' : isSent ? 'Sent' : 'Received' }</span>
                                                <span className="cv-tx-address">
                                                    { isSent ? 'To: ' + formatAddress( txTo ) : 'From: ' + formatAddress( txFrom ) }
                                                </span>
                                                <span style={{ fontSize: '11px', color: '#606080' }}>
                                                    { formatTimestamp( tx.timestamp ) } · { shortenHash( txHash ) }
                                                </span>
                                            </div>
                                            <div className="cv-tx-amount" style={{ color: tx.isError ? '#e74c3c' : isSent ? '#e74c3c' : '#3ecf8e' }}>
                                                { isSent ? '-' : '+' }{ parseFloat( tx.value || 0 ).toFixed( 6 ) } { currentNetwork.symbol }
                                            </div>
                                        </div>
                                    );
                                } ) }
                            </div>
                        ) }
                    </div>

                    { /* Invoices Panel */ }
                    <div className="cv-transactions-panel" style={{ marginTop: '20px' }}>
                        <div className="cv-panel-header"><h2>Invoices</h2></div>
                        <div className="cv-empty-state">
                            <div className="cv-empty-placeholder">
                                <div className="cv-placeholder-row"></div>
                                <div className="cv-placeholder-row short"></div>
                                <div className="cv-placeholder-row"></div>
                            </div>
                            <p className="cv-empty-title">No invoices yet</p>
                            <p className="cv-empty-subtitle">Invoice generation coming in the next update</p>
                        </div>
                    </div>
                </div>

            </div>

            { /* Deposit Modal */ }
            { showDepositModal && (
                <div className="cv-modal-overlay" onClick={ () => setShowDepositModal( false ) }>
                    <div className="cv-modal" onClick={ e => e.stopPropagation() }>
                        <div className="cv-modal-header">
                            <h3>Deposit { currentNetwork.symbol }</h3>
                            <button className="cv-modal-close" onClick={ () => setShowDepositModal( false ) }>✕</button>
                        </div>
                        { depositAddress ? (
                            <div className="cv-deposit-content">
                                <p style={{ color: '#888', fontSize: '14px', marginBottom: '16px' }}>
                                    Send { currentNetwork.symbol } to this address:
                                </p>
                                <div className="cv-address-display">
                                    <code>{ depositAddress }</code>
                                    <button className="cv-copy-btn" onClick={ copyAddress }>Copy</button>
                                </div>
                                <p style={{ color: '#888', fontSize: '12px', marginTop: '12px' }}>
                                    Only send { currentNetwork.symbol } to this address. Sending other tokens may result in permanent loss.
                                </p>
                            </div>
                        ) : (
                            <div className="cv-deposit-content">
                                <p style={{ color: '#888' }}>Please connect your wallet first.</p>
                            </div>
                        ) }
                    </div>
                </div>
            ) }

            { /* Send Modal */ }
            { showSendModal && (
                <div className="cv-modal-overlay" onClick={ () => setShowSendModal( false ) }>
                    <div className="cv-modal" onClick={ e => e.stopPropagation() }>
                        <div className="cv-modal-header">
                            <h3>Send { currentNetwork.symbol }</h3>
                            <button className="cv-modal-close" onClick={ () => setShowSendModal( false ) }>✕</button>
                        </div>
                        <div className="cv-send-content">
                            <div className="cv-field-group">
                                <label>Recipient Address</label>
                                <input
                                    type="text"
                                    className="cv-input"
                                    placeholder={ selectedNetwork === 'solana' ? 'Solana address...' : '0x...' }
                                    value={ sendTo }
                                    onChange={ e => setSendTo( e.target.value ) }
                                />
                            </div>
                            <div className="cv-field-group">
                                <label>Amount ({ currentNetwork.symbol })</label>
                                <input
                                    type="number"
                                    className="cv-input"
                                    placeholder="0.00"
                                    value={ sendAmount }
                                    onChange={ e => setSendAmount( e.target.value ) }
                                />
                                <span style={{ fontSize: '12px', color: '#888' }}>
                                    Balance: { currentBalance } { currentNetwork.symbol }
                                    { usdValue !== '$0.00' && ' ≈ ' + usdValue }
                                </span>
                            </div>
                            <button
                                className="cv-btn-primary full-width"
                                onClick={ handleSend }
                                disabled={ isSending }
                            >
                                { isSending ? 'Sending...' : 'Send ' + currentNetwork.symbol }
                            </button>
                        </div>
                    </div>
                </div>
            ) }

        </div>
    );
}

export default TransactionsPage;
